import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, FileDown } from "lucide-react";
import { useLanguage, tr } from "../i18n";
import { lp } from "../components/Navbar";
import { CTABand } from "./Shared";
import { Reveal, Kicker } from "../components/motion";
import { Badge } from "../components/portal";
import { STANDARDS } from "../data/standards";
export default function StandardDetail() {
  const {
    lang = "en",
    code
  } = useParams();
  const standard = STANDARDS.find(s => s.slug === code);
  if (!standard) return <Navigate to={lp(lang, "/standards")} replace />;
  return <div data-testid="standard-detail-page">
      <section className="border-b border-esaia-line bg-white hairline-grid">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <Reveal y={10}>
            <Link to={lp(lang, "/standards")} data-testid="back-to-standards" className="group inline-flex items-center gap-2 text-xs font-semibold text-esaia-blue">
              <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-1" />{tr("All standards")}{" "}</Link>
          </Reveal>
          <Reveal delay={0.1}>
            <Kicker className="mt-8">{tr("ESAIA Standards")}</Kicker>
            <h1 className="mt-4 font-serif text-4xl font-light tracking-tight text-esaia-ink sm:text-5xl">
              {standard.code}
            </h1>
            <p className="mt-3 max-w-2xl text-base leading-relaxed text-esaia-muted">{tr(standard.subtitle)}</p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <Reveal>
                <div className="border border-esaia-line bg-white p-8 sm:p-10" data-testid="standard-card">
                  <div className="flex items-center justify-between">
                    <Kicker>{tr("Current edition")}</Kicker>
                    <Badge tone="green" testid="standard-status-badge">{tr(standard.status)}</Badge>
                  </div>
                  <p className="mt-5 text-base font-medium text-esaia-ink">{tr(standard.title)}</p>
                  <p className="mt-3 text-sm leading-relaxed text-esaia-muted">{tr(standard.purpose)}</p>
                  <div className="mt-7 flex flex-wrap items-center gap-3 border-t border-esaia-line pt-7">
                    <span className="inline-block border border-esaia-blue/30 bg-esaia-blue/5 px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] text-esaia-blue">{tr("Version")}{" "}{standard.version}
                    </span>
                    <span className="font-mono text-xs tracking-widest text-esaia-subtle" data-testid="standard-issue-date">{tr("Issued ·")}{" "}{tr(standard.issued)}
                    </span>
                  </div>
                  <a href={standard.download} download data-testid="download-standard-button" className="group mt-8 inline-flex w-full items-center justify-center gap-2 bg-esaia-blue px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-esaia-dark">
                    <FileDown size={16} />{tr("Download the")}{" "}{standard.code}{" "}{tr("Standard")}{" "}</a>
                  <p className="mt-6 text-xs leading-relaxed text-esaia-subtle" data-testid="standard-availability-note">{tr("Detailed standard documentation is available for download. Alignment with the ESAIA framework supports responsible AI practice; it does not by itself constitute legal compliance.")}{" "}</p>
                </div>
              </Reveal>
            </div>
          </div>

          <div className="lg:col-span-8">
            {standard.groups.map(g => <div key={g.part} className="mb-20 last:mb-0">
                <Reveal>
                  <Kicker>{tr(g.part)}</Kicker>
                  <h3 className="mt-3 text-xl font-semibold tracking-tight text-esaia-ink sm:text-2xl">
                    {tr(g.title)}
                  </h3>
                </Reveal>
                <div className="mt-8 border-t border-esaia-line">
                  {g.items.map(it => <Reveal key={it.n}>
                      <div className="grid grid-cols-[56px_1fr] gap-6 border-b border-esaia-line py-10 transition-colors hover:bg-white sm:grid-cols-[88px_1fr] sm:gap-10" data-testid={`standard-section-${it.n}`}>
                        <span className="font-serif text-3xl font-light text-esaia-blue/40 sm:text-4xl">
                          {it.n}
                        </span>
                        <div className="max-w-2xl">
                          <h4 className="text-lg font-semibold text-esaia-ink">{tr(it.title)}</h4>
                          <p className="mt-3 text-sm leading-relaxed text-esaia-muted sm:text-base">
                            {tr(it.copy)}
                          </p>
                        </div>
                      </div>
                    </Reveal>)}
                </div>
              </div>)}
          </div>
        </div>
      </section>

      <CTABand title={tr("Assess your organization against the ESAIA framework.")} copy={tr("Start with the free ERI for an initial readiness picture, or request a full certification assessment.")} primary={{
      to: "/eri",
      label: tr("Take the Free ERI")
    }} secondary={{
      to: "/contact",
      label: tr("Request a Quote")
    }} />
    </div>;
}
